import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import IoTConfig from "./components/IoTConfig";
import UserPermissions from "./components/UserPermissions";
import OperationLogs from "./components/OperationLogs";

type SettingsTab = "basic" | "iot" | "permissions" | "logs";

const tabs: { key: SettingsTab; label: string; icon: string }[] = [
  { key: "basic", label: "基础配置", icon: "ri-settings-3-line" },
  { key: "iot", label: "IoT平台配置", icon: "ri-cpu-line" },
  { key: "permissions", label: "用户权限管理", icon: "ri-shield-user-line" },
  { key: "logs", label: "操作日志", icon: "ri-file-list-3-line" },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>("basic");
  const [basicSaved, setBasicSaved] = useState(false);

  const handleBasicSave = () => {
    setBasicSaved(true);
    setTimeout(() => setBasicSaved(false), 2000);
  };

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* 页面标题 */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">系统设置</h2>
            <p className="text-sm text-gray-400 mt-0.5">管理系统配置、IoT平台、用户权限和操作日志</p>
          </div>
        </div>

        {/* Tab导航 */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap -mb-px ${
                activeTab === tab.key
                  ? "border-[#E8531A] text-[#E8531A]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`}></i>
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* 基础配置 */}
        {activeTab === "basic" && (
          <div className="space-y-5 max-w-3xl">
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-800">系统基本信息</h3>
              {[
                { label: "系统名称", value: "AI智慧燃气营收管理系统", type: "text" },
                { label: "公司名称", value: "北京智慧燃气有限公司", type: "text" },
                { label: "系统版本", value: "v3.2.1", type: "text" },
                { label: "技术支持邮箱", value: "support@gascloud.com", type: "email" },
                { label: "系统Logo URL", value: "https://static.readdy.ai/image/99ef796e69792e9f0498176b3edc31a5/c291fd235bf5ccab8f3a62de9d2613bd.png", type: "text" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <label className="text-sm text-gray-500 w-36 flex-shrink-0">{item.label}</label>
                  <input
                    defaultValue={item.value}
                    type={item.type}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 bg-gray-50 focus:bg-white transition-colors"
                  />
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-800">告警阈值设置</h3>
              {[
                { label: "低余额预警 (元)", value: "100", unit: "元" },
                { label: "压力异常阈值 (MPa)", value: "0.15", unit: "MPa" },
                { label: "IoT下发失败重试次数", value: "5", unit: "次" },
                { label: "安检逾期提醒 (天)", value: "30", unit: "天" },
                { label: "保险到期提醒 (天)", value: "90", unit: "天" },
                { label: "通信中断告警 (分钟)", value: "60", unit: "分钟" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <label className="text-sm text-gray-500 w-44 flex-shrink-0">{item.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      defaultValue={item.value}
                      type="number"
                      className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                    />
                    <span className="text-sm text-gray-400">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-800">界面与显示设置</h3>
              {[
                { label: "数据刷新间隔", value: "30", unit: "秒" },
                { label: "列表默认分页大小", value: "20", unit: "条/页" },
                { label: "图表默认时间范围", value: "12", unit: "个月" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <label className="text-sm text-gray-500 w-44 flex-shrink-0">{item.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      defaultValue={item.value}
                      type="number"
                      className="w-28 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                    />
                    <span className="text-sm text-gray-400">{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleBasicSave}
                className="px-6 py-2.5 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
              >
                保存设置
              </button>
              {basicSaved && (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <i className="ri-checkbox-circle-line"></i> 设置已保存
                </span>
              )}
            </div>
          </div>
        )}

        {activeTab === "iot" && <IoTConfig />}
        {activeTab === "permissions" && <UserPermissions />}
        {activeTab === "logs" && <OperationLogs />}
      </div>
    </MainLayout>
  );
}
